// pages/Facturas.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  InputNumber,
  Divider,
  DatePicker,
  Checkbox,
  Radio,
  Descriptions,
  Collapse,
  Grid,
  Drawer,
  FloatButton
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  AppstoreOutlined,
  ExportOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  DollarOutlined,
  OrderedListOutlined,
  SortAscendingOutlined,
  PrinterOutlined,
  DownloadOutlined,
  CalendarOutlined,
  UserOutlined,
  CreditCardOutlined,
  EuroOutlined,
  FilterOutlined,
  RightOutlined,
  DownOutlined,
  UnorderedListOutlined,
  CheckOutlined,
  WalletOutlined,
  FilePdfOutlined,
  MenuOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from '../utils/dayjsConfig';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { useBreakpoint } = Grid;

const Facturas = () => {

  const screens = useBreakpoint();

  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;
  const isDesktop = screens.lg;

  // Estados principales
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detallesExpandidos, setDetallesExpandidos] = useState({});
  const [pagosExpandidos, setPagosExpandidos] = useState({});
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fechaDesde: dayjs().subtract(7, 'days'),
    fechaHasta: dayjs(),
    ejercicio: null,
    agencia: null,
    serie: [], // Cambiado a array para múltiple selección
    nfactura: '',
    snumero: '',
    contabilizada: null,
    cobrada: null
  });
  
  const [ejercicios, setEjercicios] = useState([]);
  const [series, setSeries] = useState([]); // Añadir estado para series
    
  // Estados para datos auxiliares
  const [agencias, setAgencias] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  
  // Estados para modales
  const [modalCobroVisible, setModalCobroVisible] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [modalDetallesVisible, setModalDetallesVisible] = useState(false);
  const [facturaDetalles, setFacturaDetalles] = useState(null);
  const [detallesFactura, setDetallesFactura] = useState([]);
  const [pagosFactura, setPagosFactura] = useState([]);
  
  // Form para cobro
  const [formCobro] = Form.useForm();
  
  // Estados para selección múltiple
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Estado para drawer de filtros en móvil
  const [filtrosDrawerVisible, setFiltrosDrawerVisible] = useState(false);

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'N° Factura',
      dataIndex: 'NFactura',
      key: 'NFactura',
      width: isMobile ? 100 : 120,
      sorter: (a, b) => a.NFactura - b.NFactura,
      render: (nfactura, record) => (
        <div>
          <Tag 
            color="blue" 
            style={{ 
              fontWeight: 'bold', 
              fontSize: isMobile ? '12px' : '14px',
              padding: isMobile ? '2px 6px' : '4px 8px'
            }}
          >
            {record.Serie || 'A'}/{nfactura}
          </Tag>
          {!isMobile && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              Ejercicio: {record.Ejercicio}
            </div>
          )}
        </div>
      ),
      fixed: isMobile ? false : 'left',
    },
    {
      title: 'Fecha',
      dataIndex: 'Fecha',
      key: 'Fecha',
      width: isMobile ? 90 : 120,
      render: (fecha) => (
        <div>
          <CalendarOutlined style={{ marginRight: 4, color: '#1890ff' }} />
          {fecha ? dayjs(fecha).format(isMobile ? 'DD/MM' : 'DD/MM/YYYY') : '-'}
        </div>
      ),
    },
    {
      title: 'Agencia',
      dataIndex: 'NomAgencia',
      key: 'NomAgencia',
      width: isMobile ? 120 : 200,
      render: (nomAgencia, record) => (
        <div style={{ maxWidth: isMobile ? 100 : 200 }}>
          <Text strong style={{ fontSize: isMobile ? '12px' : '13px' }}>
            {isMobile ? (record.Agencia || '') : `${record.Agencia} - ${nomAgencia || 'Sin agencia'}`}
          </Text>
          {!isMobile && record.NomSubAgen && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              Sub: {record.NomSubAgen}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Localizador',
      dataIndex: 'SNumero',
      key: 'SNumero',
      width: isMobile ? 90 : 150,
      render: (snumero) => (
        <Tag color="cyan" style={{ fontFamily: 'monospace', fontSize: isMobile ? '11px' : '14px' }}>
          {snumero || '-'}
        </Tag>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'Total',
      key: 'Total',
      width: isMobile ? 90 : 120,
      render: (total) => (
        <div style={{ textAlign: 'right' }}>
          <strong style={{ color: '#1890ff', fontSize: isMobile ? '12px' : '14px' }}>
            {total ? `${parseFloat(total).toFixed(2)} €` : '0.00 €'}
          </strong>
        </div>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: isMobile ? 100 : 120,
      render: (_, record) => {
        const pagado = parseFloat(record.TPagado || 0);
        const total = parseFloat(record.Total || 0);
        const pendiente = total - pagado;
        
        if (record.Cobrado === 'S' || pendiente <= 0) {
          return (
            <Badge
              status="success"
              text={isMobile ? 'Pagado' : 'Completo'}
              style={{ color: '#52c41a', fontWeight: 'bold', fontSize: isMobile ? '11px' : '14px' }}
            />
          );
        } else if (pagado > 0) {
          return (
            <Badge
              status="processing"
              text={isMobile ? 'Parcial' : `Parcial (${pendiente.toFixed(2)}€)`}
              style={{ color: '#1890ff', fontWeight: 'bold', fontSize: isMobile ? '11px' : '14px' }}
            />
          );
        } else {
          return (
            <Badge
              status="error"
              text={isMobile ? 'Pendiente' : 'Pendiente'}
              style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: isMobile ? '11px' : '14px' }}
            />
          );
        }
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: isMobile ? 120 : 200,
      fixed: isMobile ? false : 'right',
      render: (_, record) => (
        <Space size={isMobile ? 'small' : 'middle'}>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size={isMobile ? 'small' : 'small'}
              onClick={() => handleVerDetalles(record)}
            />
          </Tooltip>
          
          <Tooltip title="Cobrar">
            <Button
              type="primary"
              icon={<DollarOutlined />}
              size={isMobile ? 'small' : 'small'}
              onClick={() => handleCobrar(record)}
              disabled={record.Cobrado === 'S'}
            />
          </Tooltip>
          
          {!isMobile && (
            <>
              <Tooltip title="Imprimir">
                <Button
                  type="default"
                  icon={<PrinterOutlined />}
                  size="small"
                  onClick={() => handleImprimir(record)}
                />
              </Tooltip>
              
              <Popconfirm
                title="¿Anular factura?"
                description="Esta acción anulará la factura permanentemente"
                onConfirm={() => handleAnular(record.NFactura)}
                okText="Sí"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Anular">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    disabled={record.Contabilizado === 'S'}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Columnas para móvil (versión simplificada)
  const mobileColumns = columns.filter(col => 
    !['Localizador', 'Estado'].includes(col.title) || 
    col.key === 'estado' || 
    col.key === 'actions' ||
    col.dataIndex === 'SNumero'
  );

  // Cargar datos iniciales
  useEffect(() => {
    fetchEjercicios();
    fetchSeries();
    fetchFacturas();
    fetchAgencias();
    fetchFormasPago();
  }, []);

  // Función para cargar series
  const fetchSeries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/series`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSeries(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando series:', error);
      message.error('Error al cargar las series');
    }
  };

  const fetchFacturas = async (filtrosAdicionales = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const params = {
        ...filtros,
        ...filtrosAdicionales
      };

      if (params.fechaDesde) {
        params.fechaDesde = dayjs(params.fechaDesde).format('YYYY-MM-DD');
      }
      if (params.fechaHasta) {
        params.fechaHasta = dayjs(params.fechaHasta).format('YYYY-MM-DD');
      }

      if (!params.ejercicio) {
        if (ejercicios.length > 0) {
          params.ejercicio = ejercicios[0].value;
        }
      }

      // Convertir array de series a string separado por comas si hay selección
      if (params.serie && params.serie.length > 0) {
        params.serie = params.serie.join(',');
      } else {
        delete params.serie;
      }

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      if (!params.ejercicio) {
        setFacturas([]);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${BASE_URL}/api/facturas`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      const facturasData = response.data.data || [];

      console.log(" facturasData => ",facturasData);

      const facturasConDetalles = facturasData.map(factura => ({
        ...factura,
        key: factura.NFactura,
        expandible: true
      }));

      setFacturas(facturasConDetalles);
      console.log(" facturasConDetalles => ",facturasConDetalles);
      
      
      if (!isMobile) {
        message.success(`Se encontraron ${facturasConDetalles.length} facturas`);
      }

    } catch (error) {
      console.error('❌ Error cargando facturas:', error);
      message.error('Error al cargar las facturas');
      setFacturas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/agencias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAgencias(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando agencias:', error);
      message.error('Error al cargar las agencias');
    }
  };

  const fetchFormasPago = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/formaspago`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormasPago(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando formas de pago:', error);
      message.error('Error al cargar las formas de pago');
    }
  };

  const fetchDetallesFactura = async (nfactura, ejercicio) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/facturas/${nfactura}/${ejercicio}/detalles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('❌ Error cargando detalles:', error);
      return [];
    }
  };

  const fetchPagosFactura = async (nfactura, ejercicio) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/facturas/${nfactura}/${ejercicio}/pagos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('❌ Error cargando pagos:', error);
      return [];
    }
  };

  const fetchEjercicios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/facturas/ejercicios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    
      const ejerciciosData = response.data.data || [];
      setEjercicios(ejerciciosData);
    
      if (ejerciciosData.length > 0) {
        const ejercicioMasReciente = ejerciciosData[0];
        setFiltros(prev => ({
          ...prev,
          ejercicio: ejercicioMasReciente.value
        }));
      }
    
    } catch (error) {
      console.error('❌ Error cargando ejercicios:', error);
      message.error('Error al cargar los ejercicios');
    }
  };

  const handleEliminarCobro = async (factura, pago) => {
    try {
      const token = localStorage.getItem('token');
    
      if (parseFloat(pago.Pagado) > 1000) {
        Modal.confirm({
          title: 'Confirmar eliminación',
          content: `¿Está seguro de eliminar este cobro de ${parseFloat(pago.Pagado).toFixed(2)} €?`,
          okText: 'Sí, eliminar',
          cancelText: 'Cancelar',
          okButtonProps: { danger: true },
          onOk: async () => {
            await eliminarCobro(factura, pago, token);
          }
        });
      } else {
        await eliminarCobro(factura, pago, token);
      }
    
    } catch (error) {
      console.error('❌ Error eliminando cobro:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el cobro');
    }
  };

  const eliminarCobro = async (factura, pago, token) => {
    // console.log(" pago => ", pago);
    
    const response = await axios.delete(
      `${BASE_URL}/api/facturas/cobros/${pago.incre}`,
      { 
        headers: { Authorization: `Bearer ${token}` },
        params: {
          nfactura: factura.NFactura,
          serie: factura.Serie || 'A',
          ejercicio: factura.Ejercicio
        }
      }
    );

    message.success('Cobro eliminado correctamente');

    const nuevosPagos = await fetchPagosFactura(factura.NFactura);
    setPagosExpandidos(prev => ({
      ...prev,
      [factura.NFactura]: nuevosPagos
    }));

    fetchFacturas();
  };

  const handleVerDetalles = async (factura) => {
    setModalDetallesVisible(true);
    setFacturaDetalles(factura);
    
    const detalles = await fetchDetallesFactura(factura.NFactura, factura.Ejercicio);
    const pagos = await fetchPagosFactura(factura.NFactura, factura.Ejercicio);
    
    setDetallesFactura(detalles);
    setPagosFactura(pagos);
  };

  const handleCobrar = (factura) => {
    setFacturaSeleccionada(factura);
    formCobro.setFieldsValue({
      fecha: dayjs(),
      formaPago: 'EFE',
      importe: parseFloat(factura.Total) - parseFloat(factura.TPagado || 0)
    });
    setModalCobroVisible(true);
  };

  const handleImprimir = (factura) => {
    Modal.info({
      title: 'Imprimir Factura',
      content: `¿Imprimir factura ${factura.Serie || 'A'}/${factura.NFactura}?`,
      okText: 'Imprimir',
      cancelText: 'Cancelar',
      onOk: () => {
        window.open(`${BASE_URL}/api/facturas/${factura.NFactura}/imprimir`, '_blank');
      }
    });
  };

  const handleAnular = async (nfactura) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/facturas/${nfactura}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFacturas(facturas.filter(f => f.NFactura !== nfactura));
      message.success('Factura anulada correctamente');
    } catch (error) {
      console.error('❌ Error anulando factura:', error);
      message.error(error.response?.data?.error || 'Error al anular la factura');
    }
  };

  const handleCobroSubmit = async () => {
    try {
      const values = await formCobro.validateFields();
      const token = localStorage.getItem('token');
      
      const cobroData = {
        ...values,
        nfactura: facturaSeleccionada.NFactura,
        serie: facturaSeleccionada.Serie || 'A',
        ejercicio: facturaSeleccionada.Ejercicio
      };
      
      await axios.post(`${BASE_URL}/api/facturas/cobrar`, cobroData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success('Pago registrado correctamente');
      setModalCobroVisible(false);
      formCobro.resetFields();
      fetchFacturas();
      
    } catch (error) {
      console.error('❌ Error registrando pago:', error);
      message.error(error.response?.data?.error || 'Error al registrar el pago');
    }
  };

  const handleFiltrar = () => {
    if (!filtros.ejercicio) {
      message.warning('Debe seleccionar un ejercicio');
      return;
    }
    fetchFacturas();
    if (isMobile) {
      setFiltrosDrawerVisible(false);
    }
  };

  const handleLimpiarFiltros = () => {
    const fechaHasta = dayjs();
    const fechaDesde = dayjs().subtract(7, 'days');
    const ejercicioDefault = ejercicios.length > 0 ? ejercicios[0].value : null;

    setFiltros({
      fechaDesde,
      fechaHasta,
      ejercicio: ejercicioDefault,
      agencia: null,
      serie: [], // Limpiar selección múltiple
      nfactura: '',
      snumero: '',
      contabilizada: null,
      cobrada: null
    });

    setFacturas([]);
    if (isMobile) {
      setFiltrosDrawerVisible(false);
    }
  };

  const handleImprimirResumen = () => {
    if (selectedRows.length === 0) {
      message.warning('Seleccione al menos una factura para imprimir');
      return;
    }
    
    const ids = selectedRows.map(row => row.NFactura).join(',');
    window.open(`${BASE_URL}/api/facturas/imprimir-resumen?ids=${ids}`, '_blank');
  };

  const handleImprimirTodas = () => {
    if (facturas.length === 0) {
      message.warning('No hay facturas para imprimir');
      return;
    }
    
    const ids = facturas.map(f => f.NFactura).join(',');
    window.open(`${BASE_URL}/api/facturas/imprimir-todas?ids=${ids}`, '_blank');
  };

  const handleExportar = () => {
    message.info('Exportando facturas...');
  };

  const expandedRowRender = (record) => {
    const detalles = detallesExpandidos[record.NFactura] || [];
    const pagos = pagosExpandidos[record.NFactura] || [];

    return (
      <div style={{ margin: 0 }}>
        <Collapse defaultActiveKey={['detalles']}>
          <Panel header="Detalles de la Factura" key="detalles">
            <Table
              columns={[
                { title: 'Habitación', dataIndex: 'Habitacion', key: 'Habitacion', width: isMobile ? 80 : 100 },
                { title: 'Cliente', dataIndex: 'Cliente', key: 'Cliente', width: isMobile ? 120 : 200 },
                { title: 'Entrada', dataIndex: 'FechaEnt', key: 'FechaEnt', 
                  render: fecha => fecha ? dayjs(fecha).format(isMobile ? 'DD/MM' : 'DD/MM/YYYY') : '-' },
                { title: 'Salida', dataIndex: 'FechaSal', key: 'FechaSal',
                  render: fecha => fecha ? dayjs(fecha).format(isMobile ? 'DD/MM' : 'DD/MM/YYYY') : '-' },
                { title: 'Pax', dataIndex: 'Nagrupa', key: 'Nagrupa', width: 60 },
                { title: 'Días', dataIndex: 'DiasCant', key: 'DiasCant', width: 60 },
                { title: 'Precio', dataIndex: 'Precio', key: 'Precio', width: 80,
                  render: precio => `${parseFloat(precio || 0).toFixed(2)} €` },
                { title: 'Base', dataIndex: 'ImporteBase', key: 'ImporteBase', width: 80,
                  render: base => `${parseFloat(base || 0).toFixed(2)} €` },
                { title: 'IVA', dataIndex: 'ImporteIVA', key: 'ImporteIVA', width: 80,
                  render: iva => `${parseFloat(iva || 0).toFixed(2)} €` },
              ]}
              dataSource={detalles}
              rowKey="i"
              size="small"
              pagination={false}
              scroll={{ x: isMobile ? 800 : undefined }}
            />
          </Panel>
          
          <Panel header="Pagos Registrados" key="pagos">
            <Table
              columns={[
                { title: 'Fecha', dataIndex: 'Fecha', key: 'Fecha',
                  render: fecha => fecha ? dayjs(fecha).format(isMobile ? 'DD/MM' : 'DD/MM/YYYY') : '-' },
                { title: 'Forma Pago', dataIndex: 'FPago', key: 'FPago', width: isMobile ? 80 : 120 },
                { title: 'Importe', dataIndex: 'Pagado', key: 'Pagado', width: 80,
                  render: importe => `${parseFloat(importe || 0).toFixed(2)} €` },
                { title: 'Usuario', dataIndex: 'usuario', key: 'usuario', width: isMobile ? 80 : 100 },
                { title: 'Contabilizado', dataIndex: 'Contabilizado', key: 'Contabilizado', width: isMobile ? 80 : 100,
                  render: contabilizado => contabilizado ? 'Sí' : 'No' },
                { title: 'Acciones', key: 'acciones', width: isMobile ? 60 : 100,
                  render: (_, pago) => (
                    <Tooltip title="Eliminar cobro">
                      <Popconfirm
                        title="¿Eliminar este cobro?"
                        description="Esta acción eliminará el registro de pago"
                        onConfirm={() => handleEliminarCobro(record, pago)}
                        okText="Sí"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          disabled={pago.Contabilizado}
                        />
                      </Popconfirm>
                    </Tooltip>
                  ),
                }
              ]}
              dataSource={pagos}
              rowKey="Id_Unico2"
              size="small"
              pagination={false}
              scroll={{ x: isMobile ? 600 : undefined }}
            />
          </Panel>
        </Collapse>
      </div>
    );
  };

  const onExpand = async (expanded, record) => {
    if (expanded) {
      if (!detallesExpandidos[record.NFactura]) {
        const detalles = await fetchDetallesFactura(record.NFactura, record.Ejercicio);
        setDetallesExpandidos(prev => ({
          ...prev,
          [record.NFactura]: detalles
        }));
      }

      if (!pagosExpandidos[record.NFactura]) {
        const pagos = await fetchPagosFactura(record.NFactura, record.Ejercicio);
        setPagosExpandidos(prev => ({
          ...prev,
          [record.NFactura]: pagos
        }));
      }      
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
      setSelectedRows(selectedRows);
    },
  };

  // Componente de filtros para escritorio
  const filtrosBar = (
    <Card 
      size="small" 
      title={
        <Space>
          <FilterOutlined />
          <span>Filtros de Búsqueda</span>
        </Space>
      }
      style={{ marginBottom: 16 }}
      extra={
        <Space>
          <Button 
            type="primary" 
            icon={<SearchOutlined />} 
            onClick={handleFiltrar}
            loading={loading}
          >
            Buscar
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleLimpiarFiltros}
          >
            Limpiar
          </Button>
        </Space>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Ejercicio:</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="Seleccione ejercicio"
            value={filtros.ejercicio}
            onChange={(value) => setFiltros({ ...filtros, ejercicio: value })}
            allowClear={false}
            loading={ejercicios.length === 0}
          >
            {ejercicios.map(ej => (
              <Option key={ej.value} value={ej.value}>
                {ej.label}
              </Option>
            ))}
          </Select>
        </Col>      
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Fecha:</Text>
          </div>
          <RangePicker
            style={{ width: '100%' }}
            value={[filtros.fechaDesde, filtros.fechaHasta]}
            onChange={(dates) => setFiltros({
              ...filtros,
              fechaDesde: dates?.[0] || null,
              fechaHasta: dates?.[1] || null
            })}
            format="DD/MM/YYYY"
          />
        </Col>
  
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Agencia:</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="Seleccione agencia"
            value={filtros.agencia}
            onChange={(value) => setFiltros({ ...filtros, agencia: value })}
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {agencias.map(agencia => (
              <Option key={agencia.codigo} value={agencia.codigo}>
                {agencia.codigo} - {agencia.nombre}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Filtro de Serie con selección múltiple */}
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Serie:</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            mode="multiple"
            placeholder="Seleccione series"
            value={filtros.serie}
            onChange={(value) => setFiltros({ ...filtros, serie: value })}
            allowClear
            showSearch
            maxTagCount={isMobile ? 1 : 3}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} series`}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {series.map(serie => (
              <Option key={serie.serie} value={serie.serie}>
                {serie.serie}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>N° Factura:</Text>
          </div>
          <Input
            placeholder="Buscar por número"
            value={filtros.nfactura}
            onChange={(e) => setFiltros({ ...filtros, nfactura: e.target.value })}
            allowClear
          />
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Localizador:</Text>
          </div>
          <Input
            placeholder="Buscar por localizador"
            value={filtros.snumero}
            onChange={(e) => setFiltros({ ...filtros, snumero: e.target.value })}
            allowClear
          />
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Contabilizada:</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="Estado contabilización"
            value={filtros.contabilizada}
            onChange={(value) => setFiltros({ ...filtros, contabilizada: value })}
            allowClear
          >
            <Option value="S">Sí</Option>
            <Option value="N">No</Option>
          </Select>
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Cobrada:</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="Estado cobro"
            value={filtros.cobrada}
            onChange={(value) => setFiltros({ ...filtros, cobrada: value })}
            allowClear
          >
            <Option value="S">Completamente</Option>
            <Option value="P">Parcialmente</Option>
            <Option value="N">Pendiente</Option>
          </Select>
        </Col>
      </Row>
    </Card>
  );

  // Componente de filtros para móvil (Drawer)
  const filtrosDrawer = (
    <Drawer
      title={
        <Space>
          <FilterOutlined />
          <span>Filtros de Búsqueda</span>
        </Space>
      }
      placement="bottom"
      closable={true}
      onClose={() => setFiltrosDrawerVisible(false)}
      open={filtrosDrawerVisible}
      height="80%"
      className="filtros-drawer"
      extra={
        <Space>
          <Button 
            size="small"
            icon={<ReloadOutlined />} 
            onClick={handleLimpiarFiltros}
          >
            Limpiar
          </Button>
          <Button 
            type="primary" 
            size="small"
            icon={<SearchOutlined />} 
            onClick={handleFiltrar}
            loading={loading}
          >
            Buscar
          </Button>
        </Space>
      }
    >
      <div style={{ paddingBottom: 80 }}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Ejercicio:</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccione ejercicio"
              value={filtros.ejercicio}
              onChange={(value) => setFiltros({ ...filtros, ejercicio: value })}
              allowClear={false}
              loading={ejercicios.length === 0}
              size="large"
            >
              {ejercicios.map(ej => (
                <Option key={ej.value} value={ej.value}>
                  {ej.label}
                </Option>
              ))}
            </Select>
          </Col>      
          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Fecha:</Text>
            </div>
            <RangePicker
              style={{ width: '100%' }}
              value={[filtros.fechaDesde, filtros.fechaHasta]}
              onChange={(dates) => setFiltros({
                ...filtros,
                fechaDesde: dates?.[0] || null,
                fechaHasta: dates?.[1] || null
              })}
              format="DD/MM/YYYY"
              size="large"
            />
          </Col>
    
          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Agencia:</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccione agencia"
              value={filtros.agencia}
              onChange={(value) => setFiltros({ ...filtros, agencia: value })}
              allowClear
              showSearch
              size="large"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {agencias.map(agencia => (
                <Option key={agencia.codigo} value={agencia.codigo}>
                  {agencia.codigo} - {agencia.nombre}
                </Option>
              ))}
            </Select>
          </Col>

          {/* Filtro de Serie con selección múltiple para móvil */}
          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Serie:</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              mode="multiple"
              placeholder="Seleccione series"
              value={filtros.serie}
              onChange={(value) => setFiltros({ ...filtros, serie: value })}
              allowClear
              showSearch
              size="large"
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} series`}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {series.map(serie => (
                <Option key={serie.serie} value={serie.serie}>
                  {serie.serie}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>N° Factura:</Text>
            </div>
            <Input
              placeholder="Buscar por número"
              value={filtros.nfactura}
              onChange={(e) => setFiltros({ ...filtros, nfactura: e.target.value })}
              allowClear
              size="large"
            />
          </Col>
          
          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Localizador:</Text>
            </div>
            <Input
              placeholder="Buscar por localizador"
              value={filtros.snumero}
              onChange={(e) => setFiltros({ ...filtros, snumero: e.target.value })}
              allowClear
              size="large"
            />
          </Col>
          
          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Contabilizada:</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Estado contabilización"
              value={filtros.contabilizada}
              onChange={(value) => setFiltros({ ...filtros, contabilizada: value })}
              allowClear
              size="large"
            >
              <Option value="S">Sí</Option>
              <Option value="N">No</Option>
            </Select>
          </Col>
          
          <Col span={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Cobrada:</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Estado cobro"
              value={filtros.cobrada}
              onChange={(value) => setFiltros({ ...filtros, cobrada: value })}
              allowClear
              size="large"
            >
              <Option value="S">Completamente</Option>
              <Option value="P">Parcialmente</Option>
              <Option value="N">Pendiente</Option>
            </Select>
          </Col>
        </Row>
      </div>
    </Drawer>
  );

  // Estadísticas
  const estadisticas = (
    <Row gutter={[8, 8]} style={{ marginBottom: isMobile ? 12 : 24 }}>
      <Col xs={12} sm={6}>
        <Card size="small" style={{ textAlign: 'center' }}>
          <Statistic 
            title="Total" 
            value={facturas.length}
            prefix={<FileTextOutlined />}
            valueStyle={{ color: '#1890ff', fontSize: isMobile ? '14px' : '24px' }}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card size="small" style={{ textAlign: 'center' }}>
          <Statistic 
            title="Importe" 
            value={facturas.reduce((sum, f) => sum + parseFloat(f.Total || 0), 0).toFixed(2)}
            suffix="€"
            prefix={<EuroOutlined />}
            valueStyle={{ color: '#52c41a', fontSize: isMobile ? '14px' : '24px' }}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card size="small" style={{ textAlign: 'center' }}>
          <Statistic 
            title="Pendiente" 
            value={facturas.reduce((sum, f) => {
              const total = parseFloat(f.Total || 0);
              const pagado = parseFloat(f.TPagado || 0);
              return sum + (total - pagado);
            }, 0).toFixed(2)}
            suffix="€"
            prefix={<DollarOutlined />}
            valueStyle={{ color: '#ff4d4f', fontSize: isMobile ? '14px' : '24px' }}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card size="small" style={{ textAlign: 'center' }}>
          <Statistic 
            title="Selección" 
            value={selectedRows.length}
            suffix={`/ ${facturas.length}`}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#722ed1', fontSize: isMobile ? '14px' : '24px' }}
          />
        </Card>
      </Col>
    </Row>
  );

  // Botón flotante para móvil
  const mobileFloatButton = isMobile && (
    <FloatButton
      icon={<FilterOutlined />}
      type="primary"
      style={{ right: 16, bottom: 80 }}
      onClick={() => setFiltrosDrawerVisible(true)}
      tooltip="Filtros"
    />
  );

  return (
    <>
      <PageContainer
        title="Gestión de Facturas"
        subtitle={isMobile ? `${facturas.length} facturas` : `${facturas.length} facturas encontradas`}
        icon={<FileTextOutlined />}
        loading={loading}
        onRefresh={() => fetchFacturas()}
        badgeCount={facturas.length}
        badgeStatus="processing"
        extra={
          !isMobile && (
            <Space>
              <Button
                icon={<PrinterOutlined />}
                onClick={handleImprimirResumen}
                disabled={selectedRows.length === 0}
              >
                Imprimir Selección
              </Button>
              <Button
                icon={<FilePdfOutlined />}
                onClick={handleImprimirTodas}
                disabled={facturas.length === 0}
              >
                Imprimir Todas
              </Button>
              <Button
                icon={<ExportOutlined />}
                onClick={handleExportar}
              >
                Exportar
              </Button>
            </Space>
          )
        }
        cardStyle={{
          borderRadius: isMobile ? '8px' : '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Filtros - Desktop */}
        {!isMobile && filtrosBar}
        
        {/* Botón para mostrar filtros en móvil */}
        {isMobile && (
          <div style={{ marginBottom: 12 }}>
            <Button 
              type="primary" 
              icon={<FilterOutlined />}
              onClick={() => setFiltrosDrawerVisible(true)}
              block
              size="large"
            >
              Mostrar Filtros
            </Button>
          </div>
        )}

        {/* Filtros - Mobile Drawer */}
        {filtrosDrawer}
        
        {/* Tabla */}
        <Table
          columns={isMobile ? mobileColumns : columns}
          dataSource={facturas}
          rowKey="NFactura"
          loading={loading}
          scroll={{ x: isMobile ? 600 : 1500, y: isMobile ? 400 : 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size={isMobile ? 'small' : 'middle'}
          rowSelection={isMobile ? undefined : rowSelection}
          expandable={{
            expandedRowRender: isMobile ? expandedRowRender : expandedRowRender,
            onExpand,
            rowExpandable: record => record.expandible,
          }}
          locale={{
            emptyText: filtros.fechaDesde && filtros.fechaHasta && filtros.ejercicio 
              ? 'No se encontraron facturas con los filtros aplicados' 
              : 'Seleccione filtros y haga clic en "Buscar" para ver facturas'
          }}
          pagination={{
            size: isMobile ? 'small' : 'default',
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            showTotal: (total) => `Total ${total} facturas`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
        
        {/* Botón flotante de filtros en móvil */}
        {mobileFloatButton}
        
        {/* Acciones rápidas en móvil */}
        {isMobile && facturas.length > 0 && (
          <div style={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            background: 'white', 
            padding: '8px 12px',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            zIndex: 100,
            display: 'flex',
            gap: '8px'
          }}>
            <Button 
              icon={<PrinterOutlined />}
              onClick={handleImprimirTodas}
              style={{ flex: 1 }}
              size="small"
            >
              Imprimir
            </Button>
            <Button 
              icon={<FilePdfOutlined />}
              onClick={handleExportar}
              style={{ flex: 1 }}
              size="small"
            >
              Exportar
            </Button>
          </div>
        )}
      </PageContainer>

      {/* Modal de cobro */}
      <Modal
        title={`Cobrar Factura ${facturaSeleccionada?.Serie || 'A'}/${facturaSeleccionada?.NFactura}`}
        open={modalCobroVisible}
        onOk={handleCobroSubmit}
        onCancel={() => {
          setModalCobroVisible(false);
          formCobro.resetFields();
        }}
        width={isMobile ? '95%' : 500}
        okText="Registrar Pago"
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: isMobile ? 20 : 100 }}
      >
        {facturaSeleccionada && (
          <div style={{ marginBottom: 20 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Total Factura">
                <strong>{parseFloat(facturaSeleccionada.Total || 0).toFixed(2)} €</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Ya Pagado">
                {parseFloat(facturaSeleccionada.TPagado || 0).toFixed(2)} €
              </Descriptions.Item>
              <Descriptions.Item label="Pendiente">
                <strong style={{ color: '#ff4d4f' }}>
                  {(parseFloat(facturaSeleccionada.Total || 0) - parseFloat(facturaSeleccionada.TPagado || 0)).toFixed(2)} €
                </strong>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
        
        <Form
          form={formCobro}
          layout="vertical"
        >
          <Form.Item
            name="fecha"
            label="Fecha de Pago"
            rules={[{ required: true, message: 'Seleccione la fecha' }]}
          >
            <DatePicker 
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              size={isMobile ? 'large' : 'default'}
            />
          </Form.Item>
          
          <Form.Item
            name="formaPago"
            label="Forma de Pago"
            rules={[{ required: true, message: 'Seleccione forma de pago' }]}
          >
            <Select 
              placeholder="Seleccione forma de pago"
              size={isMobile ? 'large' : 'default'}
            >
              {formasPago
                .filter(fp => fp.mostrar !== false)
                .map(fp => (
                  <Option key={fp.codigo} value={fp.codigo}>
                    {fp.codigo} - {fp.descripcion}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="importe"
            label="Importe a Pagar"
            rules={[
              { required: true, message: 'Ingrese el importe' },
              {
                validator: (_, value) => {
                  const max = facturaSeleccionada 
                    ? parseFloat(facturaSeleccionada.Total) - parseFloat(facturaSeleccionada.TPagado || 0)
                    : 0;
                  
                  if (value > max) {
                    return Promise.reject(new Error(`El importe no puede ser mayor a ${max.toFixed(2)} €`));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.01}
              max={facturaSeleccionada 
                ? parseFloat(facturaSeleccionada.Total) - parseFloat(facturaSeleccionada.TPagado || 0)
                : 0
              }
              step={0.01}
              precision={2}
              addonAfter="€"
              size={isMobile ? 'large' : 'default'}
            />
          </Form.Item>
          
          <Form.Item
            name="observaciones"
            label="Observaciones"
          >
            <TextArea
              placeholder="Observaciones adicionales..."
              rows={2}
              size={isMobile ? 'large' : 'default'}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de detalles */}
      <Modal
        title={`Detalles Factura ${facturaDetalles?.Serie || 'A'}/${facturaDetalles?.NFactura}`}
        open={modalDetallesVisible}
        onCancel={() => setModalDetallesVisible(false)}
        width={isMobile ? '95%' : 900}
        footer={[
          <Button key="close" onClick={() => setModalDetallesVisible(false)}>
            Cerrar
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => handleImprimir(facturaDetalles)}
          >
            Imprimir Factura
          </Button>
        ]}
        style={{ top: isMobile ? 20 : 100 }}
        bodyStyle={{ padding: isMobile ? 12 : 24 }}
      >
        {facturaDetalles && (
          <div>
            {/* Información de la factura */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions 
                title="Información de la Factura" 
                column={isMobile ? 1 : 2} 
                bordered 
                size={isMobile ? 'small' : 'default'}
              >
                <Descriptions.Item label="Número">{facturaDetalles.Serie || 'A'}/{facturaDetalles.NFactura}</Descriptions.Item>
                <Descriptions.Item label="Ejercicio">{facturaDetalles.Ejercicio}</Descriptions.Item>
                <Descriptions.Item label="Fecha">{dayjs(facturaDetalles.Fecha).format('DD/MM/YYYY')}</Descriptions.Item>
                <Descriptions.Item label="Reserva">{facturaDetalles.NReserva}</Descriptions.Item>
                <Descriptions.Item label="Agencia">{facturaDetalles.Agencia} - {facturaDetalles.NomAgencia}</Descriptions.Item>
                <Descriptions.Item label="Localizador">{facturaDetalles.SNumero}</Descriptions.Item>
                <Descriptions.Item label="Contabilizada">
                  <Tag color={facturaDetalles.Contabilizado === 'S' ? 'green' : 'red'}>
                    {facturaDetalles.Contabilizado === 'S' ? 'Sí' : 'No'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Cobrada">
                  <Tag color={facturaDetalles.Cobrado === 'S' ? 'green' : facturaDetalles.TPagado > 0 ? 'orange' : 'red'}>
                    {facturaDetalles.Cobrado === 'S' ? 'Completo' : facturaDetalles.TPagado > 0 ? 'Parcial' : 'Pendiente'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  <strong>{parseFloat(facturaDetalles.Total || 0).toFixed(2)} €</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Pagado">
                  {parseFloat(facturaDetalles.TPagado || 0).toFixed(2)} €
                </Descriptions.Item>
                <Descriptions.Item label="Base Imponible">
                  {parseFloat(facturaDetalles.BaseImponible || 0).toFixed(2)} €
                </Descriptions.Item>
                <Descriptions.Item label="IVA">
                  {parseFloat(facturaDetalles.ImporteIVA || 0).toFixed(2)} €
                </Descriptions.Item>
              </Descriptions>
            </Card>
            
            {/* Detalles de la factura */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={isMobile ? 5 : 5}>Detalles de Líneas</Title>
              <Table
                columns={[
                  { title: 'Habitación', dataIndex: 'Habitacion', key: 'Habitacion', width: isMobile ? 70 : 100 },
                  { title: 'Cliente', dataIndex: 'Cliente', key: 'Cliente', width: isMobile ? 100 : 150 },
                  { title: 'Entrada', dataIndex: 'FechaEnt', key: 'FechaEnt',
                    render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                  { title: 'Salida', dataIndex: 'FechaSal', key: 'FechaSal',
                    render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                  { title: 'Pax', dataIndex: 'Nagrupa', key: 'Nagrupa', width: 60 },
                  { title: 'Días', dataIndex: 'DiasCant', key: 'DiasCant', width: 60 },
                  { title: 'Precio', dataIndex: 'Precio', key: 'Precio',
                    render: precio => `${parseFloat(precio || 0).toFixed(2)} €` },
                  { title: 'Base', dataIndex: 'ImporteBase', key: 'ImporteBase',
                    render: base => `${parseFloat(base || 0).toFixed(2)} €` },
                  { title: 'IVA', dataIndex: 'ImporteIVA', key: 'ImporteIVA',
                    render: iva => `${parseFloat(iva || 0).toFixed(2)} €` },
                ]}
                dataSource={detallesFactura}
                rowKey="i"
                size="small"
                pagination={false}
                scroll={{ x: isMobile ? 800 : undefined }}
              />
            </Card>
            
            {/* Pagos */}
            {pagosFactura.length > 0 && (
              <Card size="small">
                <Title level={isMobile ? 5 : 5}>Pagos Registrados</Title>
                <Table
                  columns={[
                    { title: 'Fecha', dataIndex: 'Fecha', key: 'Fecha',
                      render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                    { title: 'Forma Pago', dataIndex: 'FPago', key: 'FPago', width: isMobile ? 80 : 120 },
                    { title: 'Importe', dataIndex: 'Pagado', key: 'Pagado',
                      render: importe => `${parseFloat(importe || 0).toFixed(2)} €` },
                    { title: 'Usuario', dataIndex: 'usuario', key: 'usuario', width: isMobile ? 80 : 100 },
                    { title: 'Contabilizado', dataIndex: 'Contabilizado', key: 'Contabilizado',
                      render: contabilizado => contabilizado ? 'Sí' : 'No' },
                    {
                      title: 'Acciones',
                      key: 'acciones',
                      width: isMobile ? 60 : 120,
                      render: (_, pago) => (
                        <Tooltip title="Eliminar cobro">
                          <Popconfirm
                            title="¿Eliminar este cobro?"
                            description="Esta acción eliminará el registro de pago"
                            onConfirm={() => handleEliminarCobro(facturaDetalles, pago)}
                            okText="Sí"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                          >
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              size="small"
                              disabled={pago.Contabilizado}
                            />
                          </Popconfirm>
                        </Tooltip>
                      ),
                    },                      
                  ]}
                  dataSource={pagosFactura}
                  rowKey="Id_Unico2"
                  size="small"
                  pagination={false}
                  scroll={{ x: isMobile ? 600 : undefined }}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

// Componente Progress para estado de pago
const Progress = ({ percent, size = 'default', status = 'normal' }) => {
  const colors = {
    success: '#52c41a',
    exception: '#ff4d4f',
    active: '#1890ff',
    normal: '#1890ff'
  };
  
  return (
    <div style={{ 
      width: '100%', 
      backgroundColor: '#f5f5f5', 
      borderRadius: 4,
      overflow: 'hidden',
      height: size === 'small' ? 6 : 8
    }}>
      <div style={{
        width: `${Math.min(percent, 100)}%`,
        backgroundColor: colors[status],
        height: '100%',
        transition: 'width 0.3s'
      }} />
    </div>
  );
};

export default Facturas;